<template>
	<van-popup v-model:show="show" position="bottom" round class="square-share-panel-popup">
		<div class="square-share-panel">
			<input
				ref="copyLinkInputRef"
				type="text"
				readonly
				tabindex="-1"
				class="square-share-panel__copy-input"
				aria-hidden="true"
			/>
			<h3 class="square-share-panel__title">{{ t("square.detail.share") }}</h3>
			<div class="square-share-panel__list">
				<button
					v-for="(item, index) in shareList"
					:key="index"
					type="button"
					class="square-share-panel__item"
					@click="onShareItem(item)"
					:aria-label="
						t('square.detail.shareTo', {
							platform: item.text || t('square.detail.sharePlatform'),
						})
					"
				>
					<img :src="item.icon" :alt="item.text || t('square.detail.share')" />
					<span>{{ item.text }}</span>
				</button>
			</div>
			<button type="button" class="square-share-panel__copy-link" @click.stop="handleCopyLink">
				{{ t("square.detail.copyLink") }}
			</button>
		</div>
	</van-popup>
</template>

<script setup>
import { ref } from "vue";
import { useI18n } from "~/composables/useI18n";
import showMessage from "~/utils/showMessage";
import { copySync } from "~/utils/clipboard";

defineProps({
	shareList: {
		type: Array,
		default: () => [],
	},
});

const emit = defineEmits(["share"]);

const show = defineModel("show", { type: Boolean, default: false });

const { t } = useI18n();
const copyLinkInputRef = ref(null);

function onShareItem(item) {
	emit("share", item);
}

function handleCopyLink() {
	if (!import.meta.client) return;
	const url = window.location.href;

	emit("share", {
		text: "copy_link",
		shareFn: () => {
			const input = copyLinkInputRef.value;
			if (input) {
				input.value = url;
				input.focus({ preventScroll: true });
				input.select();
				input.setSelectionRange(0, url.length);
				if (document.execCommand("copy")) {
					showMessage(t("square.detail.copyLinkSuccess"), "success");
					return;
				}
			}

			if (copySync(url)) {
				showMessage(t("square.detail.copyLinkSuccess"), "success");
				return;
			}

			navigator.clipboard
				?.writeText(url)
				.then(() => showMessage(t("square.detail.copyLinkSuccess"), "success"))
				.catch(() => showMessage(t("square.detail.copyLinkFailed"), "error"));
		},
	});
}
</script>

<style lang="scss" scoped>
.square-share-panel {
	position: relative;
	padding: 32px 30px 48px;

	&__copy-input {
		position: absolute;
		width: 1px;
		height: 1px;
		padding: 0;
		border: 0;
		opacity: 0;
		pointer-events: none;
	}

	&__title {
		margin: 0 0 32px;
		font-size: 36px;
		font-weight: 700;
		color: #333333;
	}

	&__list {
		display: grid;
		grid-template-columns: repeat(4, 1fr);
		gap: 24px 16px;
	}

	&__item {
		display: flex;
		flex-direction: column;
		align-items: center;
		gap: 12px;
		padding: 0;
		border: none;
		background: transparent;
		cursor: pointer;

		img {
			width: 88px;
			height: 88px;
			object-fit: cover;
			border-radius: 50%;
			display: block;
		}

		span {
			font-size: 28px;
			color: #333333;
			white-space: nowrap;
		}
	}

	&__copy-link {
		display: block;
		width: 100%;
		font-size: 36px;
		color: #333333;
		border: 1px solid #333333;
		border-radius: 16px;
		text-align: center;
		padding: 20px 0;
		font-weight: 500;
		margin-top: 40px;
		background: transparent;
		cursor: pointer;
	}
}
</style>
